import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export interface ChatUnreadEntry {
  conversationId: string;
  count: number;
  name: string;
  avatarUrl: string | null;
  lastActivity: string;
  lastContent: string | null;
}

interface State {
  byId: Record<string, ChatUnreadEntry>;
  hydrated: boolean;
}

const initialState: State = { byId: {}, hydrated: false };

const slice = createSlice({
  name: 'chatUnread',
  initialState,
  reducers: {
    bulkHydrate(state, action: PayloadAction<ChatUnreadEntry[]>) {
      state.byId = {};
      for (const e of action.payload) state.byId[e.conversationId] = e;
      state.hydrated = true;
    },
    incrementForConversation(
      state,
      action: PayloadAction<{
        conversationId: string;
        lastActivity: string;
        name?: string;
        avatarUrl?: string | null;
        content?: string | null;
      }>
    ) {
      const { conversationId, lastActivity, name, avatarUrl, content } = action.payload;
      const existing = state.byId[conversationId];
      if (existing) {
        existing.count += 1;
        existing.lastActivity = lastActivity;
        if (content !== undefined) existing.lastContent = content;
        // Backfill name/avatar if the prior entry was created without them.
        if (name && existing.name === 'Conversation') existing.name = name;
        if (avatarUrl && !existing.avatarUrl) existing.avatarUrl = avatarUrl;
      } else {
        state.byId[conversationId] = {
          conversationId,
          count: 1,
          name: name ?? 'Conversation',
          avatarUrl: avatarUrl ?? null,
          lastActivity,
          lastContent: content ?? null,
        };
      }
    },
    clearConversation(state, action: PayloadAction<string>) {
      delete state.byId[action.payload];
    },
  },
});

export const { bulkHydrate, incrementForConversation, clearConversation } =
  slice.actions;

export const selectUnreadEntries = (s: any): ChatUnreadEntry[] => {
  const byId = (s.chatUnread as State).byId;
  return Object.values(byId).sort((a, b) =>
    a.lastActivity < b.lastActivity ? 1 : -1
  );
};

export const selectTotalUnread = (s: any): number =>
  Object.values((s.chatUnread as State).byId).reduce(
    (sum, e) => sum + e.count,
    0
  );

export const selectChatUnreadHydrated = (s: any): boolean =>
  (s.chatUnread as State).hydrated;

export default slice.reducer;
